import { gql, useQuery } from "@apollo/client";
import type {
    BatchGetConfigEnableResponse,
    BatchGetConfigEnableVariables
} from '../types/CommonConfigTypes';

// 批量检查功能是否启用的查询（统一使用批量查询，单个查询作为特例处理）
export const BATCH_GET_CONFIG_ENABLE = gql`
    query BatchGetConfigEnable($configKeys: [String!]!) {
        batchGetConfigEnable(configKeys: $configKeys) {
            results {
                configKey
                enabled
            }
        }
    }
`;

export type {
    BatchGetConfigEnableResponse,
    BatchGetConfigEnableVariables
};

/**
 * 检查指定配置是否启用的 Hook（单个配置）
 * @param configKey 配置键（如 "address.coordinate_feedback.gray_config"）
 * @returns {Object} 包含 isEnabled, loading, error 状态
 */
export function useConfigEnable(configKey: string) {
    const { data, loading, error } = useQuery<BatchGetConfigEnableResponse, BatchGetConfigEnableVariables>(
        BATCH_GET_CONFIG_ENABLE,
        {
            variables: { configKeys: [configKey] },
            fetchPolicy: 'cache-and-network',
        }
    );

    const isEnabled = data?.batchGetConfigEnable?.results?.find(
        entry => entry.configKey === configKey
    )?.enabled ?? false;

    return {
        isEnabled,
        loading,
        error
    };
}

/**
 * 批量检查多个配置是否启用的 Hook
 * @param configKeys 配置键数组
 * @returns {Object} 包含 configMap（配置键到启用状态的映射）, loading, error 状态
 */
export function useBatchConfigEnable(configKeys: string[]) {
    const { data, loading, error } = useQuery<BatchGetConfigEnableResponse, BatchGetConfigEnableVariables>(
        BATCH_GET_CONFIG_ENABLE,
        {
            variables: { configKeys },
            fetchPolicy: 'cache-and-network',
        }
    );

    // 将结果转换为 Map，方便按 key 快速查找
    const configMap = new Map<string, boolean>();
    data?.batchGetConfigEnable?.results?.forEach(result => {
        configMap.set(result.configKey, result.enabled);
    });

    // 提供一个辅助函数，用于获取指定 key 的启用状态
    const isEnabled = (configKey: string): boolean => {
        return configMap.get(configKey) ?? false;
    };

    return {
        configMap,
        isEnabled,
        loading,
        error
    };
}

// 通用配置类型定义
export interface CommonConfigType {
    id: string;
    configKey: string;
    configValue: string;
    description?: string;
    configType: string;
    isActive: boolean;
    createdBy?: number;
    updatedBy?: number;
    createdAt?: number;
    updatedAt?: number;
}

// GraphQL查询响应类型
export interface GetCommonConfigByKeyQuery {
    getCommonConfigByKey: CommonConfigType | null;
}

// GraphQL查询变量类型
export interface GetCommonConfigByKeyVariables {
    configKey: string;
}

// 根据key查询通用配置
export const GET_COMMON_CONFIG_BY_KEY = gql`
  query GetCommonConfigByKey($configKey: String!) {
    getCommonConfigByKey(configKey: $configKey) {
      id
      configKey
      configValue
      description
      configType
      isActive
      createdBy
      updatedBy
      createdAt
      updatedAt
    }
  }
`;

// Hook for getting common config by key
export const useGetCommonConfigByKey = (configKey: string) => {
    return useQuery<GetCommonConfigByKeyQuery, GetCommonConfigByKeyVariables>(
        GET_COMMON_CONFIG_BY_KEY,
        {
            variables: { configKey },
            skip: !configKey, // 如果没有configKey则跳过查询
        }
    );
};

// 根据ID查询通用配置的响应类型
export interface GetCommonConfigByIdQuery {
    getCommonConfigById: CommonConfigType | null;
}

// 根据ID查询通用配置的变量类型
export interface GetCommonConfigByIdVariables {
    id: string;
}

// 根据ID查询通用配置
export const GET_COMMON_CONFIG_BY_ID = gql`
  query GetCommonConfigById($id: ID!) {
    getCommonConfigById(id: $id) {
      id
      configKey
      configValue
      description
      configType
      isActive
      createdBy
      updatedBy
      createdAt
      updatedAt
    }
  }
`;

// Hook for getting common config by ID
export const useGetCommonConfigById = (id: string) => {
    return useQuery<GetCommonConfigByIdQuery, GetCommonConfigByIdVariables>(
        GET_COMMON_CONFIG_BY_ID,
        {
            variables: { id },
            skip: !id, // 如果没有id则跳过查询
        }
    );
};

// 检查当前登录用户的warehouseId是否在灰度配置中的查询响应类型
export interface CheckWarehouseIdInGrayConfigQuery {
    checkWarehouseIdInGrayConfig: boolean;
}

// 检查warehouseId是否在灰度配置中的查询变量类型
export interface CheckWarehouseIdInGrayConfigVariables {
    configKey: string;
    warehouseId?: number | null;
}

// 检查warehouseId是否在灰度配置中
export const CHECK_WAREHOUSE_ID_IN_GRAY_CONFIG = gql`
    query CheckWarehouseIdInGrayConfig($configKey: String!, $warehouseId: Long) {
        checkWarehouseIdInGrayConfig(configKey: $configKey, warehouseId: $warehouseId)
    }
`;

// Hook for checking warehouseId in gray config
export const useCheckWarehouseIdInGrayConfig = (configKey: string, warehouseId?: number | null) => {
    return useQuery<CheckWarehouseIdInGrayConfigQuery, CheckWarehouseIdInGrayConfigVariables>(
        CHECK_WAREHOUSE_ID_IN_GRAY_CONFIG,
        {
            variables: { configKey, warehouseId },
            skip: !configKey, // 如果没有configKey则跳过查询
            fetchPolicy: 'cache-and-network',
        }
    );
};

